import { Baby, Mail, Calendar, Plane, Lightbulb, ClipboardCheck, Flower2, Flame, Languages, BookUser, Clapperboard, Radiation, HeartHandshake, MessageCircleQuestion, Coffee, Repeat } from 'lucide-react'
import type { AppCategoryId, AppDefinition } from './types'

/** Explore-page sections, in display order. */
export const APP_CATEGORIES: { id: AppCategoryId; label: string; description: string }[] = [
  {
    id: 'family',
    label: 'Family & home',
    description: 'Run the household together',
  },
  {
    id: 'planning',
    label: 'Planning & money',
    description: 'Stay ahead of plans, ideas, and expenses',
  },
  {
    id: 'growth',
    label: 'Personal growth',
    description: 'Build habits and learn new things',
  },
  {
    id: 'work',
    label: 'Work & fieldwork',
    description: 'Tools for the job',
  },
]
import { babyTrackerBottomNav } from '@/modules/baby-tracker/nav'
import { emailBottomNav } from '@/modules/email/nav'
import { plannerBottomNav } from '@/modules/planner/nav'
import { tripExpensesBottomNav } from '@/modules/trip-expenses/nav'
import { ideasBottomNav } from '@/modules/ideas/nav'
import { choresBottomNav } from '@/modules/chores/nav'
import { koreanBottomNav } from '@/modules/korean/nav'
import { creatorBottomNav } from '@/modules/creator/nav'
import { coregulateBottomNav } from '@/modules/co-regulate/nav'
import { curiousBottomNav } from '@/modules/curious/nav'
import { habitLoopBottomNav } from '@/modules/habit-loop/nav'

// Set `hidden: true` to retire an app; APP_REGISTRY (below) is the visible subset.
const ALL_APPS: AppDefinition[] = [
  {
    id: 'baby-tracker',
    name: 'Baby Tracker',
    description: 'Track feeding schedules, diaper changes, and sleep',
    icon: Baby,
    route: '/apps/baby-tracker',
    color: 'bg-[#e8eae2]',
    iconColor: 'text-[#7d9462]',
    category: 'family',
    bottomNav: babyTrackerBottomNav,
  },
  {
    id: 'email',
    name: 'Email',
    description: 'AI-powered email prioritization and filtering',
    icon: Mail,
    route: '/apps/email',
    color: 'bg-blue-100',
    iconColor: 'text-blue-600',
    category: 'work',
    bottomNav: emailBottomNav,
    hidden: true,
  },
  {
    id: 'planner',
    name: 'Planner',
    description: 'Weekly goals and daily planning with Big Rocks methodology',
    icon: Calendar,
    route: '/apps/planner',
    color: 'bg-purple-100',
    iconColor: 'text-purple-600',
    category: 'planning',
    bottomNav: plannerBottomNav,
    hidden: true,
  },
  {
    id: 'trip-expenses',
    name: 'Trip Expenses',
    description: 'Split and settle expenses with your travel group',
    icon: Plane,
    route: '/apps/trip-expenses',
    color: 'bg-amber-100',
    iconColor: 'text-amber-600',
    category: 'planning',
    bottomNav: tripExpensesBottomNav,
  },
  {
    id: 'ideas',
    name: 'Ideas',
    description: 'Capture every idea, keep the ones with energy',
    icon: Lightbulb,
    route: '/apps/ideas',
    color: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    category: 'planning',
    bottomNav: ideasBottomNav,
    settingsRoute: '/apps/ideas/settings',
  },
  {
    id: 'chores',
    name: 'Chores',
    description: 'Capture chores and tackle them by priority',
    icon: ClipboardCheck,
    route: '/apps/chores',
    color: 'bg-teal-100',
    iconColor: 'text-teal-600',
    category: 'family',
    bottomNav: choresBottomNav,
  },
  {
    id: 'sharon',
    name: 'Sharon',
    description: 'A personal space for notes',
    icon: Flower2,
    route: '/apps/sharon',
    color: 'bg-rose-100',
    iconColor: 'text-rose-600',
    category: 'family',
    settingsRoute: '/apps/sharon/settings',
  },
  {
    id: 'korean',
    name: 'Korean',
    description: 'Capture, expand, and study Korean words & phrases',
    icon: Languages,
    route: '/apps/korean',
    color: 'bg-sky-100',
    iconColor: 'text-sky-600',
    category: 'growth',
    bottomNav: koreanBottomNav,
    defaultEnabled: false,
  },
  {
    id: 'pushups',
    name: 'Pushups',
    description: 'Daily count & streak tracker',
    icon: Flame,
    route: '/apps/pushups',
    color: 'bg-orange-100',
    iconColor: 'text-orange-600',
    category: 'growth',
    settingsRoute: '/apps/pushups/settings',
  },
  {
    id: 'radon',
    name: 'Radon',
    description: 'Organize radon test jobs, devices, and records by address',
    icon: Radiation,
    route: '/apps/radon',
    color: 'bg-lime-100',
    iconColor: 'text-lime-700',
    category: 'work',
    defaultEnabled: false,
  },
  {
    id: 'names',
    name: 'Names',
    description: 'Remember the names of people you meet',
    icon: BookUser,
    route: '/apps/names',
    color: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    category: 'growth',
    defaultEnabled: false,
  },
  {
    id: 'co-regulate',
    name: 'Co-Regulate',
    description: 'In-the-moment coaching to ride out tantrums together',
    icon: HeartHandshake,
    route: '/apps/co-regulate',
    color: 'bg-teal-100',
    iconColor: 'text-teal-600',
    category: 'family',
    bottomNav: coregulateBottomNav,
    settingsRoute: '/apps/co-regulate/settings',
    defaultEnabled: false,
  },
  {
    id: 'curious',
    name: 'Curious',
    description: 'Real-time follow-up questions for pop culture chats',
    icon: MessageCircleQuestion,
    route: '/apps/curious',
    color: 'bg-pink-100',
    iconColor: 'text-pink-600',
    category: 'growth',
    bottomNav: curiousBottomNav,
    defaultEnabled: false,
  },
  {
    id: 'creator',
    name: 'Creator Corner',
    description: 'Watch, suggest, and cheer on the YouTube journey together',
    icon: Clapperboard,
    route: '/apps/creator',
    color: 'bg-red-100',
    iconColor: 'text-red-600',
    category: 'family',
    bottomNav: creatorBottomNav,
    settingsRoute: '/apps/creator/settings',
    defaultEnabled: false,
  },
  {
    id: 'habit-loop',
    name: 'Habit Loop',
    description: 'Find the cue behind a bad habit, then swap the routine',
    icon: Repeat,
    route: '/apps/habit-loop',
    color: 'bg-[#ddebe4]',
    iconColor: 'text-[#486c5e]',
    category: 'growth',
    bottomNav: habitLoopBottomNav,
    defaultEnabled: false,
  },
  {
    id: 'birch-admin',
    name: 'Birch Site',
    description: 'Edit the Birch Coffee Co. website — schedule, menu, and announcements',
    icon: Coffee,
    route: '/apps/birch-admin',
    color: 'bg-[#dbeafe]',
    iconColor: 'text-[#0093cf]',
    category: 'work',
    defaultEnabled: false,
  },
]

export const APP_REGISTRY: AppDefinition[] = ALL_APPS.filter((app) => !app.hidden)

export function getAppById(id: string): AppDefinition | undefined {
  return APP_REGISTRY.find((app) => app.id === id)
}

/**
 * Map a stored `enabled_modules` object (module id -> bool; missing key = the
 * app's own `defaultEnabled`, which is true for established apps and false for
 * newly launched ones) to the list of enabled, non-retired app ids. Retired
 * (hidden) apps never appear — they're excluded from APP_REGISTRY — so
 * navigating to one is always treated as disabled.
 */
export function enabledIdsFromMap(map: Record<string, boolean>): string[] {
  return APP_REGISTRY.filter((app) => map[app.id] ?? app.defaultEnabled ?? true).map(
    (app) => app.id
  )
}

/**
 * Resolve the enabled app ids for a user given their (possibly missing)
 * preferences. A brand-new account with no row is scoped to its signup app when
 * one was stamped into metadata, otherwise everything is on. Shared by the
 * dashboard layout, middleware, and the client store so they always agree.
 */
export function enabledModuleIdsForUser(
  enabledModules: Record<string, boolean> | null | undefined,
  signupApp?: unknown
): string[] {
  if (enabledModules) return enabledIdsFromMap(enabledModules)
  if (typeof signupApp === 'string' && getAppById(signupApp)) return [signupApp]
  return APP_REGISTRY.filter((app) => app.defaultEnabled !== false).map((app) => app.id)
}
